import React, { useState, useEffect } from "react";
import { Save, Tag, User, Calendar, Clock, AlignLeft } from "lucide-react";
import FormHeader from "../Common/FormHeader"; 

const FormWorkshop = ({ onBack, onSave, initialData = null }) => {
  // Definisi State Awal yang Stabil
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    instructor: "",
    date: "",
    time: "",
    location: "",
    description: "",
    image: null
  });

  // Sinkronisasi data tanpa mengubah ukuran array dependensi
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        title: initialData.title || "",
        category: initialData.category || "",
        instructor: initialData.speaker || initialData.instructor || "",
        date: initialData.date || "",
        time: initialData.time || "",
        location: initialData.location || "",
        description: initialData.description || "",
        image: initialData.image || null
      });
    } else {
      setFormData({
        title: "", category: "", instructor: "",
        date: "", time: "", location: "",
        description: "", image: null
      });
    }
  }, [initialData]); // Dependensi tetap konstan (objek atau null)

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="p-6 space-y-6 bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm border border-stone-100 dark:border-white/5 animate-in fade-in duration-500">
      
      {/* HEADER DENGAN TOMBOL BACK */}
      <FormHeader 
        title={initialData ? "Edit Workshop" : "Create New Workshop"}
        subtitle="Fill in the details for Siloam Cardiac Summit session"
        onBack={onBack} 
      />

      <form onSubmit={handleSubmit} className="space-y-8 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          <div className="space-y-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Workshop Title</label>
              <div className="relative">
                <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text" required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all"
                  placeholder="e.g. Advanced Cardiology"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Instructor Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="text"
                  value={formData.instructor}
                  onChange={(e) => setFormData({...formData, instructor: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Category</label>
                <select 
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none focus:border-blue-500 transition-all"
                >
                  <option value="">Select Category</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Surgery">Surgery</option>
                  <option value="Nursing">Nursing</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                  <input 
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 rounded-xl text-sm font-normal outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Time Slot</label>
              <div className="relative">
                <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={14} />
                <input 
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 dark:bg-slate-800 border border-stone-200 dark:border-white/10 rounded-xl text-sm font-normal outline-none"
                />
              </div>
            </div>
          </div>

          <div className="md:col-span-2 flex flex-col gap-1.5">
            <label className="text-[11px] font-medium text-stone-500 uppercase tracking-wider">Description</label>
            <div className="relative">
              <AlignLeft className="absolute left-4 top-4 text-stone-400" size={14} />
              <textarea 
                rows="4"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full pl-10 pr-4 py-4 bg-stone-50 dark:bg-slate-800 border border-stone-200 rounded-xl text-sm font-normal outline-none resize-none leading-relaxed focus:border-blue-500"
                placeholder="Workshop details..."
              />
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-stone-50 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onBack}
            className="px-8 py-3 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-50 transition-all"
          >
            Discard
          </button>
          <button 
            type="submit"
            className="px-10 py-3 bg-[#0F172A] text-white rounded-xl text-sm font-semibold shadow-lg hover:bg-slate-800 transition-all flex items-center gap-2"
          >
            <Save size={18} />
            {initialData ? "Update Workshop" : "Save Workshop"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormWorkshop;